/**
 * Ops Notifications Bell — floating top-right alert center.
 *
 * Polls open alerts every 30 s and shows an unread badge. Clicking reveals a
 * dropdown with the five most recent open alerts and a link to the full
 * Alerts page. Cheap, self-contained, no websocket dependency required.
 */
import { useEffect, useRef, useState } from 'react';
import { Bell, AlertTriangle, MapPin, WifiOff, Package, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { opsApi } from '@/services/api';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

const POLL_MS = 30_000;

const TYPE_META = {
  route_deviation: { icon: MapPin,        color: 'text-amber-500 dark:text-amber-400', label: 'Route deviation' },
  device_offline:  { icon: WifiOff,       color: 'text-red-500 dark:text-red-400',     label: 'Device offline' },
  weight_drop:     { icon: Package,       color: 'text-blue-500 dark:text-blue-400',   label: 'Weight variance' },
  temperature_breach: { icon: AlertTriangle, color: 'text-rose-500 dark:text-rose-400', label: 'Temp breach' },
};

function iconFor(type) {
  const m = TYPE_META[type];
  return m ? <m.icon className={`w-4 h-4 shrink-0 ${m.color}`} /> : <AlertTriangle className="w-4 h-4 shrink-0 text-slate-400" />;
}

export default function NotificationsBell() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOpen = async () => {
      try {
        const res = await opsApi.getAlerts({ status: 'open' });
        if (!cancelled) setAlerts(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        /* silent — retries next tick */
      }
    };
    fetchOpen();
    const t = setInterval(fetchOpen, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Click-outside to close
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const count = alerts.length;
  const critical = alerts.some(a => a.level === 'critical');

  return (
    <div className="fixed top-3 right-4 z-40" ref={wrapRef} data-testid="ops-notifications-bell">
      <button
        type="button"
        onClick={() => setOpen(x => !x)}
        aria-label={`${count} open alerts`}
        className={cn(
          "relative rounded-full h-10 w-10 flex items-center justify-center shadow-md border transition",
          isDark
            ? "bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50",
          count > 0 && (critical ? "ring-2 ring-red-500/60" : "ring-2 ring-amber-500/50")
        )}
        data-testid="ops-notifications-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span
            className={cn(
              "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white",
              critical ? "bg-red-500" : "bg-amber-500"
            )}
            data-testid="ops-notifications-badge"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            "absolute right-0 mt-2 w-96 rounded-xl shadow-2xl border overflow-hidden",
            isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-200 text-slate-900"
          )}
          data-testid="ops-notifications-panel"
        >
          <div className={cn("px-4 py-3 flex items-center justify-between border-b",
            isDark ? "border-slate-800" : "border-slate-200")}>
            <div>
              <p className="text-sm font-semibold">Open alerts</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                {count === 0 ? 'All clear — nothing needs attention' : `${count} alert${count === 1 ? '' : 's'} waiting`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={cn("rounded p-1", isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500")}
              aria-label="close"
              data-testid="ops-notifications-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <ul className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <li className={cn("px-4 py-8 text-center text-sm", isDark ? "text-slate-500" : "text-slate-500")}>
                No open alerts right now
              </li>
            ) : (
              alerts.slice(0, 5).map(a => (
                <li
                  key={a.id}
                  className={cn(
                    "px-4 py-3 border-b flex gap-3 items-start",
                    isDark ? "border-slate-800 hover:bg-slate-800/60" : "border-slate-100 hover:bg-slate-50"
                  )}
                  data-testid={`ops-notification-item-${a.id}`}
                >
                  {iconFor(a.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-xs font-semibold uppercase tracking-wider",
                        a.level === 'critical'
                          ? 'text-red-500 dark:text-red-400'
                          : a.level === 'warning'
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-blue-600 dark:text-blue-400')}>
                        {a.level}
                      </span>
                      <span className={cn("text-[10px]", isDark ? "text-slate-500" : "text-slate-400")}>
                        {a.triggered_at ? new Date(a.triggered_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <p className={cn("text-sm mt-0.5 truncate", isDark ? "text-slate-100" : "text-slate-900")}>
                      {a.message || (TYPE_META[a.type]?.label || a.type)}
                    </p>
                    {a.device_uid && (
                      <p className={cn("text-[11px] mt-0.5 font-mono", isDark ? "text-slate-500" : "text-slate-400")}>
                        {a.device_uid}
                      </p>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>

          <Link
            to="/ops/alerts"
            onClick={() => setOpen(false)}
            className={cn(
              "flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-t",
              isDark ? "text-teal-400 border-slate-800 hover:bg-slate-800" : "text-teal-600 border-slate-200 hover:bg-slate-50"
            )}
            data-testid="ops-notifications-view-all"
          >
            View all alerts <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
